import type { ApplicationConfig } from '@angular/core';
import {
  APP_INITIALIZER,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { provideRouter, Router, RouteReuseStrategy } from '@angular/router';
import { IonicRouteStrategy, isPlatform, provideIonicAngular } from '@ionic/angular';
import { IonicStorageModule } from '@ionic/storage-angular';
import { StoreModule } from '@ngrx/store';
import { kitAuthInterceptor, provideKitAuth, provideKitOverlay } from '@rdlabo/ionic-angular-kit';
import { provideKitFirebase, provideKitFirebaseAnalytics } from '@rdlabo/ionic-angular-kit/auth-firebase';
import { provideLiveUpdateReadiness } from '@rdlabo/ionic-angular-kit/live-update';
import { providePhotoEditor } from '@rdlabo/ionic-angular-photo-editor';
import { createTuiImageEditor } from '@rdlabo/ionic-angular-photo-editor/editor/tui';
import { loadCapacitorPhotoCamera } from '@rdlabo/ionic-angular-photo-editor/file/capacitor';
import { iosTransitionAnimation } from '@rdlabo/ionic-theme-ios26';
import * as Sentry from '@sentry/angular';

import packageInfo from '../../../../../package.json';
import { AuthService } from '../../../shared/src/auth/auth.service';
import { environment } from '../../../shared/src/environments/environment';
import { provideOdssHttp } from '../../../shared/src/http-config';
import { provideOdssSignalForms } from '../../../shared/src/config/signal-forms';
import { reducers } from '../reducer';
import { routes } from './app.routes';

Sentry.init({
  dsn: 'https://c37e0298b45b0575a3e2479cc54af345@o4507378227412992.ingest.us.sentry.io/4510724391763968',
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  tracesSampleRate: 0.01,
  tracePropagationTargets: [
    'localhost',
    'capacitor://localhost',
    'http://localhost',
    'https://localhost',
    'https://mobile.odss.jp',
  ],
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 0.3,
  environment: environment.production ? 'production' : 'development',
  enabled: environment.production,
  release: packageInfo.version,
  beforeSend(event, hint) {
    const error = hint.originalException;
    const ignoreMessages = [
      'Connection to Indexed Database server lost',
      'auth/network-request-failed',
      'auth/quota-exceeded',
      'auth/user-not-found',
      'auth/too-many-requests',
      'auth/internal-error',
      'auth/email-already-in-use',
      'auth/wrong-password',
      'auth/invalid-email',
    ];
    if (error instanceof Error && ignoreMessages.some((msg) => error.message?.includes(msg))) {
      return null;
    }
    return event;
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideOdssSignalForms(),
    provideLiveUpdateReadiness(),
    provideRouter(routes),
    provideKitFirebase({ firebaseConfig: environment.firebase }),
    provideKitFirebaseAnalytics(),
    importProvidersFrom(BrowserModule, StoreModule.forRoot(reducers), IonicStorageModule.forRoot()),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    AuthService,
    provideHttpClient(withXhr(), withInterceptors([kitAuthInterceptor])),
    provideOdssHttp(),
    provideIonicAngular({
      backButtonText: '',
      scrollAssist: true,
      swipeBackEnabled: false,
      useSetInputAPI: true,
      navAnimation: isPlatform('ios') ? iosTransitionAnimation : undefined,
    }),
    provideKitOverlay({ labels: { close: '閉じる', cancel: 'キャンセル' } }),
    providePhotoEditor({
      createImageEditor: createTuiImageEditor,
      loadCamera: loadCapacitorPhotoCamera,
    }),
    provideKitAuth(() => {
      const auth = inject(AuthService);
      return {
        authState: () => auth.isAuth$(),
        redirects: {
          whenAuthorized: '/',
          whenConfirming: '/auth/confirm',
          whenNotConfirming: '/auth/signin',
          whenUnauthorized: 'auth',
        },
      };
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
};
